// Shapes the Finance ▸ QuickBooks screen reads from /api/finance/qbo/*.
// Every amount arrives as a rendered string — the browser never adds money up.
// Every sentence arrives written by the server; the screen only lays it out.
import { ApiError } from "@/lib/api";

export type Entity = "invoices" | "bills" | "payments" | "credit_memos" | "refunds" | "payouts";

export const ENTITIES: Entity[] = ["invoices", "bills", "payments", "credit_memos", "refunds", "payouts"];

export function entityFromParam(raw: string | null): Entity {
  return (ENTITIES as string[]).includes(raw ?? "") ? (raw as Entity) : "invoices";
}

/** Plain words per document type: tab title, nouns, and the two named columns. */
export const NOUN: Record<Entity, { one: string; many: string; Title: string; doc: string; who: string; empty: string }> = {
  invoices: { one: "invoice", many: "invoices", Title: "Invoices", doc: "Invoice", who: "Client", empty: "Invoices show up here once they're sent to the client." },
  bills: { one: "bill", many: "bills", Title: "Bills", doc: "Bill", who: "Supplier", empty: "Bills show up here once they're approved." },
  payments: { one: "payment", many: "payments", Title: "Payments", doc: "Invoice paid", who: "Client", empty: "Payments show up here once the money has arrived." },
  credit_memos: { one: "credit", many: "credits", Title: "Credits", doc: "Credit on", who: "Client or supplier", empty: "Credits to clients and from suppliers show up here." },
  refunds: { one: "card refund", many: "card refunds", Title: "Card refunds", doc: "Refund on invoice", who: "Client", empty: "Card refunds show up here once Stripe completes them." },
  payouts: { one: "Stripe payout", many: "Stripe payouts", Title: "Stripe payouts", doc: "Arrived", who: "What's in it", empty: "Stripe payouts show up here once they reach the bank (from the start date Jake picks)." },
};

/** Where each document type's own Flow page is — the Back link (ux review). */
export const BACK_LINK: Record<Entity, { to: string; label: string }> = {
  invoices: { to: "/finance/invoicing", label: "Invoicing" },
  bills: { to: "/finance/billing", label: "Billing" },
  payments: { to: "/finance/invoicing", label: "Invoicing" },
  credit_memos: { to: "/finance/invoicing", label: "Invoicing" },
  refunds: { to: "/finance/invoicing", label: "Invoicing" },
  payouts: { to: "/finance/dashboard", label: "Finance" },
};

export interface QboStatus {
  configured: boolean;
  connected: boolean;
  exportAllowed?: boolean;
  exportLockedReason?: string | null;
}

export type QueueState = "not_sent" | "failed" | "held";

export interface QueueRow {
  id: string;
  docNumber: string;
  name: string;
  date: string;
  amount: string;
  syncStatus: string;
  lastError: string | null;
  /** The same failure in ≤ ~60 characters, for the table cell. */
  lastErrorHeadline?: string | null;
  detail?: string | null;
  state?: QueueState;
  stateLabel?: string;
  /** false = held — can't be sent from the queue. */
  sendable?: boolean;
}

export interface Queue {
  entity: Entity;
  period: string | null;
  count: number;
  total: string;
  /** Only the rows that are ready to send (the default selection). */
  ready?: { count: number; total: string };
  failedCount?: number;
  heldCount?: number;
  rows: QueueRow[];
}

export type ExportCounts = Record<Entity, { ready: number; failed: number; held: number }>;

export interface RowResult {
  id: string;
  ok: boolean;
  docNumber?: string;
  deduped?: boolean;
  /** false → it reached the QuickBooks TEST company only. The row is left
   *  untouched and still waiting to go to the real books — never show it as sent. */
  stamped?: boolean;
  /** In QuickBooks, but QuickBooks booked a different tax — never "Sent". */
  taxMismatch?: string;
  /** Waiting (for its invoice, its card fee …) — not a failure. */
  held?: boolean;
  error?: string;
}

export interface ExportResponse {
  results: RowResult[];
  sent: number;
  sentToSandbox?: number;
  needsTaxCheck?: number;
  held?: number;
  failed: number;
}

export const TEST_COMPANY_ONLY = "Sent to the test company only — still waiting for your books";

export interface HistoryRow {
  id: string;
  entityType: string;
  entityId?: string;
  docNumber: string | null;
  name: string | null;
  status: string;
  /** "production" | "sandbox" — a sandbox success is not money in the books. */
  environment?: string;
  message: string | null;
  /** The same in ≤ ~60 characters. */
  headline?: string | null;
  occurredAt: string;
}

export type IssueCategory =
  | "changed_after_sync"
  | "tax_mismatch"
  | "permanently_failed"
  | "failed"
  | "held"
  | "waiting"
  | "online_not_invoiced"
  | "refund_unsent";

export type IssueOwner = "you" | "jake" | "stripe" | "flow";

export interface SyncIssue {
  key: string;
  kind: string;
  id: string;
  environment: "production" | "sandbox";
  category: IssueCategory;
  title: string;
  /** ≤ ~60 characters — what's wrong. */
  headline: string;
  amount: string | null;
  since: string;
  reason: string;
  nextStep: string;
  owner: IssueOwner;
  action: "retry" | "check_again" | null;
  canMarkHandled: boolean;
  attempts: number | null;
  href: string | null;
  /** The technical text — only ever copied for Jake, never shown. */
  technical: string | null;
}

export interface SyncIssueList {
  environment: "production" | "sandbox";
  count: number;
  counts: Record<IssueCategory, number>;
  issues: SyncIssue[];
  categories: Record<IssueCategory, string>;
  owners: Record<IssueOwner, string>;
  urgent: IssueCategory[];
}

export interface IssueRetryResult {
  ok: boolean;
  outcome: string;
  message: string;
  environment?: string;
}

export function errText(e: unknown, fallback: string): string {
  if (e instanceof ApiError) {
    const b = e.body as { error?: string } | null;
    return b?.error ?? fallback;
  }
  return fallback;
}

export function formatDate(s: string): string {
  const d = /^\d{4}-\d{2}-\d{2}$/.test(s) ? new Date(s + "T00:00:00") : new Date(s);
  if (Number.isNaN(d.getTime())) return s;
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
}

/** "Oct 18, 2026, 3:04 PM". */
export function formatDateTime(s: string): string {
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return s;
  return d.toLocaleString(undefined, { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" });
}

/** A document number as people say it: "#70412" for numbers, as-is otherwise
 *  ("(no bill number)", a vendor's "INV-4412"). */
export function docLabel(docNumber: string): string {
  return /^\d+$/.test(docNumber) ? `#${docNumber}` : docNumber;
}

